import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { __ } from '../../i18n/l10n';
import { SLUG_CATEGORY, SLUG_PRODUCT } from '../../styles/slugs';
import { AttributeValue } from '../api/queries/useAttributes';

export type PriceRange = {
  min?: number;
  max?: number;
};

export type Sorting = 'ASC' | 'DESC' | 'RECOM' | undefined;

export const sortings: { value: Sorting; key: string }[] = [
  { value: 'ASC', key: __('Price ascending') },
  { value: 'DESC', key: __('Price descending') },
];

type Filters = {
  attributes: AttributeValue[];
  range?: PriceRange;
  sorting: Sorting;
  onlyAvailable: boolean;
};

interface IContext {
  attributes: AttributeValue[];
  range?: PriceRange;
  onlyAvailable: boolean;
  setOnlyAvailable: (b: boolean) => void;
  sorting: Sorting;
  setSorting: (sort: Sorting) => void;
  filters: Filters;
  resetFilters: () => void;
  saveFilters: () => void;
  discardFilters: () => void;
  toggleRange: (range: PriceRange) => void;
  toggleAttribute: (attribute: AttributeValue) => void;
  refetch: boolean;
  setRefetch: (e: boolean) => void;
  subCatList: number[];
  setSubCatList: (cat: number[]) => void;
  setCurrentCat: (s?: string) => void;
}

const FiltersContext = createContext<IContext | undefined>(undefined);
export const initialFilters = {
  attributes: [],
  onlyAvailable: false,
  range: undefined,
  sorting: undefined,
};

export const FiltersContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [refetch, setRefetch] = useState<boolean>(true);
  const [currentCat, setCurrentCat] = useState<string | undefined>();
  const [subCatList, setSubCatList] = useState<number[]>([]);
  const [attributes, setAttributes] = useState<AttributeValue[]>([]);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [range, setRange] = useState<PriceRange | undefined>();
  const [onlyAvailable, setOnlyAvailable] = useState<boolean>(false);
  const [sorting, setSorting] = useState<Sorting>();
  const location = useLocation();

  const toggleRange = (r: PriceRange) => {
    if (range?.min === r.min && range?.max === r.max) setRange(undefined);
    else setRange(r);
  };

  const toggleAttribute = (attribute: AttributeValue) => {
    const selected =
      attributes.find((attr) => attr.id === attribute.id) !== undefined;

    selected
      ? setAttributes([
          ...attributes.filter((attr) => attr.id !== attribute.id),
        ])
      : setAttributes([...attributes, attribute]);
  };

  const saveFilters = useCallback(() => {
    setFilters({ attributes, onlyAvailable, range, sorting });
    setRefetch(true);
  }, [sorting, attributes, range, onlyAvailable]);

  const resetFilters = useCallback(() => {
    setAttributes([]);
    setRange(undefined);
    setOnlyAvailable(false);
    setSorting(undefined);
    setFilters(initialFilters);
    setRefetch(true);
  }, []);

  const discardFilters = () => {
    setAttributes(filters.attributes);
    setRange(filters.range);
    setOnlyAvailable(filters.onlyAvailable);
    setSorting(filters.sorting);
  };

  useEffect(() => {
    setFilters(initialFilters);
    discardFilters();
    setRefetch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCatList, currentCat]);

  useEffect(() => {
    if (
      (location.pathname.includes(SLUG_CATEGORY) &&
        currentCat &&
        !location.pathname.includes(currentCat)) ||
      (!location.pathname.includes(SLUG_CATEGORY) &&
        !location.pathname.includes(SLUG_PRODUCT))
    ) {
      setFilters(initialFilters);
      setAttributes([]);
      setRange(undefined);
      setOnlyAvailable(false);
      setSorting(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <FiltersContext.Provider
      value={{
        setOnlyAvailable,
        attributes,
        onlyAvailable,
        range,
        sorting,
        setSorting,
        filters,
        discardFilters,
        toggleRange,
        toggleAttribute,
        refetch,
        setRefetch,
        subCatList,
        setSubCatList,
        setCurrentCat,
        saveFilters,
        resetFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);
  if (!context)
    throw new Error(
      'No FiltersContext.Provider found when calling useFiltersContext',
    );
  return context;
};
